import type { ReactElement } from "react"
import { useState } from "react"

import { useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { Pagination, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import type { WorkflowInputSchema } from "../../types/custom/projects"
import type { WorkflowConstantFeature } from "../../types/generated/api/WorkflowConstantFeature"
import { BaseTable } from "./BaseTable"

type InputFeaturesProps = {
  data: WorkflowConstantFeature[]
  isLoading: boolean
}

export function ConstantsHistory(props: Readonly<InputFeaturesProps>): ReactElement {
  const { t } = useTranslation("tables")
  const { data, isLoading } = props

  const [page, setPage] = useState<number>(0)

  const rowsPerPage = 10

  const paginatedConstantsWithIndex = data
    // First, paginate the features by slicing based on current page and rows per page
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const columns: MRT_ColumnDef<WorkflowInputSchema>[] = [
    {
      header: t("constantsHistory.columns.constant"),
      accessorKey: "name",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]

        return (
          <Grid item xs={12}>
            <Typography variant="p">{paginatedConstantsWithIndex?.length > 0 ? constant?.name : ""}</Typography>
          </Grid>
        )
      },
    },
    {
      header: t("constantsHistory.columns.value"),
      accessorKey: "value",
      Cell: ({ row }) => {
        const constant = paginatedConstantsWithIndex[row.index]

        return (
          <Grid item xs={12}>
            <Typography variant="p">{`${constant?.value ?? ""} `}</Typography>
          </Grid>
        )
      },
    },
  ]

  return (
    <Grid item container xs gap={2} alignContent={"space-between"}>
      <Grid item xs>
        <BaseTable
          disableToolbar
          columns={(columns as MRT_ColumnDef[]) ?? []}
          data={paginatedConstantsWithIndex ?? []}
          isLoading={isLoading}
          title={t("constantsHistory.title")}
          enableOrdering={false}
          newStyling
          disablePagination
        />
      </Grid>

      {data.length > 0 && (
        <Pagination
          page={page + 1}
          onPageChange={(page: number) => setPage(page - 1)}
          pageSize={rowsPerPage}
          totalCount={data.length}
          variant={"complex"}
          size="small"
          hidePageSize
        />
      )}
    </Grid>
  )
}
